<template>
  <v-dialog :value="value" :width="step !== 3 ? '1100px' : '600px'" persistent @input="$emit('input', $event)">
    <v-card v-if="value">
      <div v-if="step === 1">
        <v-card-title class="px-4">
          <span class="section-title">Ajustar Cashback</span> <br />
          <p class="subtitle pt-1" style="word-break: break-word">
            Aqui você pode além de conferir o estado atual da carteira do consumidor pode também creditar ou debitar. Os ajustes realizados são refletidos no
            aplicativo do consumidor imediatamente, lembre-se que cashback ativado não pode ser debitado, apenas o saldo da carteira pode ser alterado.
          </p>
        </v-card-title>
        <v-card-text class="pb-0 pt-2 dialog-content">
          <div class="d-flex justify-space-between">
            <div class="d-flex search-container">
              <mf-search
                v-model="documentNo"
                :vMask="['###.###.###-##', '##.###.###/####-##']"
                :disabled="disableEdition"
                label="CPF/CNPJ"
                placeholder="Buscar cliente por CPF/CNPJ"
                outlined
                dense
                :error-messages="userNotFoundError"
              />
              <mf-button label="Buscar" icon="search" :disabled="!isSearchEnabled" class="ml-2" :loading="loadingUser" @click="searchUser" />
            </div>
            <mf-button label="Importar ajuste em massa" icon="upload" text color="marca-0-l40" class="unset-capitalize" @click="step = 2" />
          </div>
          <v-container fluid class="pa-0">
            <span class="table-card-title">Dados do cliente</span>

            <v-row v-if="user" class="pa-0 pt-0">
              <v-col cols="12" class="pt-0">
                <v-data-table :headers="userTableHeaders" :items="[user]" :loading="loadingUser" hide-default-footer>
                  <template v-slot:item.document_no="{ item }">
                    {{ user.document_no | cpfCnpj | hifen }}
                  </template>
                  <template v-slot:item.name="{ item }">
                    {{ user.name | hifen }}
                  </template>
                  <template v-slot:item.activated_amount="{ item }">
                    {{ user_cashback.activated_amount | localeString(2) | currency }}
                  </template>
                  <template v-slot:item.inactivated_amount="{ item }">
                    {{ user_cashback.inactivated_amount | localeString(2) | currency }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>

            <span class="table-card-title">Configuração do Ajuste</span>

            <v-row v-if="user" class="pa-0 pt-3">
              <v-col cols="3" class="py-0">
                <v-select v-model="adjustmentType" dense :disabled="!userCashbackFound" outlined label="Tipo de Ajuste" :items="adjustmentTypeItems" />
              </v-col>
              <v-col cols="3" class="py-0">
                <v-text-field
                  v-model="adjustmentValue"
                  v-money="vMoney"
                  dense
                  :disabled="!userCashbackFound"
                  :error-messages="adjustmentValueError"
                  prefix="R$"
                  label="Valor do Ajuste"
                  outlined
                  @keydown="$event.key === '-' ? $event.preventDefault() : null"
                />
              </v-col>
              <v-col cols="6" class="py-0">
                <v-select
                  v-model="adjustmentReason"
                  dense
                  :disabled="!userCashbackFound || adjustmentType === 'DEBIT'"
                  outlined
                  label="Motivo de Ajuste"
                  :items="adjustmentReasons"
                />
              </v-col>

              <v-col cols="3">
                <date-picker
                  v-if="adjustmentType === 'CREDIT'"
                  :min="minDate"
                  :date.sync="expiresAt"
                  :error-messages="expiresAtError"
                  outlined
                  label="Data de expiração"
                  dense
                  :disabled="!userCashbackFound"
                />
              </v-col>
            </v-row>

            <span class="table-card-title">Resultado do Ajuste</span>

            <v-row v-if="user" class="pa-0 pt-3 pb-3">
              <v-col cols="6" class="py-0">
                <span>Cashback na Carteira - Anterior: {{ user_cashback.inactivated_amount | localeString(2) | currency }}</span>
              </v-col>
              <v-col cols="6" class="py-0">
                <span>Cashback na Carteira - Posterior: {{ adjustedCashback | localeString(2) | currency }}</span>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <mf-action-buttons
            class="py-2"
            :primary-button="{
              text: 'Ajustar',
              action: submit,
              isVisible: true,
              isLoading: loading,
              isDisabled: loadingUser || !userCashbackFound,
            }"
            :extra-button="{
              text: 'Cancelar',
              action: closeDialog,
              isVisible: true,
            }"
          />
        </v-card-actions>
      </div>
      <import-dynamic-csv-card
        v-if="step === 2"
        title="Importar ajuste de Cashback"
        import-label="Importar arquivo"
        :fields="importFields"
        :is-modal-open="value"
        load-objects
        :required-all-of="['cpf', 'value', 'reason', 'expires_at']"
        :required-any-of="[]"
        cashback
        :loading="loading"
        list-text="Todos os seguintes campos são obrigatórios:"
        @close-dialog="step = 1"
      >
        <template v-slot:custom-button>
          <mf-button icon="tune" label="Ajustar manualmente" text color="marca-0-l40" class="unset-capitalize" @click="step = 1" />
        </template>
      </import-dynamic-csv-card>
      <approve-cashback-dialog-content
        v-if="step === 3"
        :adjust-cashback-data="adjustCashbackData"
        :loading="loading"
        @approve="setEmailsAndApprove"
        @cancel="cancelCashbackResults"
        @close="closeDialog"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { VMoney } from 'v-money'
import { SEARCH_USER_QUERY, SEARCH_USER_CASHBACK_QUERY, MUTATION_ADJUST_CASHBACK, MUTATION_BULK_ADJUST_CASHBACK } from '@/modules/mobile/cashback/graphql'
import { DateTime } from 'luxon'
import { required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
const initialState = () => ({
  documentNo: '',
  user: {},
  user_cashback: {},
  adjustmentType: 'CREDIT',
  adjustmentValue: '',
  adjustmentReason: 'Reajuste',
  loading: false,
  loadingUser: false,
  userCashbackFound: false,
  userNotFound: false,
  expiresAt: '',
})

const parseMoney = (money) => {
  if (!money) return 0.0
  return parseFloat(money.replace('.', '').replace(',', '.'))
}

const VALID_DOCUMENT_NO_LENGTHS = [11, 14]

export default {
  name: 'AdjustCashbackDialog',

  components: {
    ImportDynamicCsvCard: () => import('@/components/ImportDynamicCsvCard.vue'),
    ApproveCashbackDialogContent: () => import('./ApproveCashbackDialogContent.vue'),
    DatePicker: () => import('@/components/DatePicker.vue'),
  },

  directives: {
    money: VMoney,
  },

  props: {
    value: Boolean,
  },

  provide: function () {
    return {
      objectSelected: this.importCashback,
      updateRequiredColumns: () => {},
    }
  },

  data: () => ({
    documentNo: '',
    user: {},
    user_cashback: {},
    adjustmentType: 'CREDIT',
    adjustmentValue: '',
    adjustmentReason: 'Reajuste',
    loading: false,
    loadingUser: false,
    userCashbackFound: false,
    userNotFound: false,
    step: 1,
    adjustCashbackData: {},
    expiresAt: '',
  }),

  computed: {
    importFields() {
      let csvFields = [
        { text: 'CPF/CNPJ do consumidor', value: 'cpf', label: 'CPF/CNPJ' },
        { text: 'Valor (modelo "0,00")', value: 'value', label: 'Valor' },
        { text: 'Motivo do ajuste (texto: Reajuste ou Premiação)', value: 'reason', label: 'Motivo do ajuste' },
        { text: 'Data de expiração (dd/mm/aaaa)', value: 'expires_at', label: 'Data de expiração', subLabel: 'obrigatório apenas para creditar cashback' },
      ]
      return csvFields
    },
    cashbackLineLimit() {
      if (this.$getIsAdmin()) {
        return 50000
      }
      return 5000
    },
    vMoney() {
      return { decimal: ',', thousands: '.', precision: 2, masked: false }
    },
    userTableHeaders() {
      return [
        {
          text: 'CPF/CNPJ',
          width: '25%',
          sortable: false,
          value: 'document_no',
        },
        {
          text: 'Nome',
          width: '25%',
          sortable: false,
          value: 'name',
        },
        {
          text: 'Cashback Ativado',
          width: '25%',
          sortable: false,
          value: 'activated_amount',
        },
        {
          text: 'Cashback na Carteira',
          width: '25%',
          sortable: false,
          value: 'inactivated_amount',
        },
      ]
    },
    userNotFoundError() {
      if (this.userNotFound) return 'Usuário não encontrado'
      return ''
    },
    adjustmentValueError() {
      if (!this.$v.adjustmentValue) return ''
      if (this.$v.adjustmentValue.$error) {
        if (!this.$v.adjustmentValue.min) return 'Ajuste precisa ser maior que R$ 0,00'
        if (!this.$v.adjustmentValue.max) return 'Cashback na carteira insuficiente.'
      }
      return ''
    },
    expiresAtError() {
      if (this.$v.expiresAt.$error) return 'Insira a data de expiração'
      return ''
    },
    disableEdition() {
      return this.loadingUser || this.loading
    },
    isSearchEnabled() {
      return VALID_DOCUMENT_NO_LENGTHS.includes(this.cleanDocumentNo.length)
    },
    cleanDocumentNo() {
      return this.documentNo.replace(/[^0-9]/g, '')
    },
    adjustmentTypeItems() {
      return [
        {
          text: 'Creditar',
          value: 'CREDIT',
        },
        {
          text: 'Debitar',
          value: 'DEBIT',
        },
      ]
    },
    adjustmentReasons() {
      return ['Reajuste', 'Premiação']
    },
    currentInactivatedAmount() {
      return this.user_cashback?.inactivated_amount ?? 0
    },
    adjustedCashback() {
      if (!Object.keys(this.user_cashback)) return null
      return this.user_cashback?.inactivated_amount + this.parsedAdjustmentValue
    },
    parsedAdjustmentValue() {
      const value = parseMoney(this.adjustmentValue)
      switch (this.adjustmentType) {
        case 'CREDIT':
          return value
        case 'DEBIT':
          return -value
        default:
          return 0
      }
    },
    minDate() {
      return DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd')
    },
  },

  watch: {
    value(value) {
      if (value) {
        this.$v.$reset()
        Object.assign(this.$data, initialState())
      }
    },
    adjustmentType(newValue) {
      if (newValue === 'DEBIT') this.adjustmentReason = 'Reajuste'
    },
  },

  methods: {
    cancelCashbackResults() {
      this.savedVariables = {}
      this.step = 2
    },
    setEmailsAndApprove(emails) {
      this.importCashback({}, emails, true)
    },
    async importCashback({ file, selectedColumns }, emails, commit = false) {
      this.loading = true
      if (!commit) {
        this.savedVariables = {
          file,
          columns: {
            document_no: selectedColumns.findIndex((label) => label === 'cpf'),
            adjustment_amount: selectedColumns.findIndex((label) => label === 'value'),
            adjustment_reason: selectedColumns.findIndex((label) => label === 'reason'),
            expires_at: selectedColumns.findIndex((label) => label === 'expires_at'),
          },
        }
      } else {
        this.savedVariables.emails = emails
      }
      try {
        await this.$apollo
          .mutate({
            mutation: MUTATION_BULK_ADJUST_CASHBACK,
            variables: {
              ...this.savedVariables,
              commit,
            },
            context: {
              uri: this.$microservicesUrls['cashbackUpload'],
            },
          })
          .then(({ data: { bulkAdjustCashback } }) => {
            if (commit) {
              this.$emit('adjust')
              this.closeDialog()
              this.$snackbar({ message: 'Relatório enviado com sucesso.', snackbarColor: 'success' })
            } else {
              this.step = 3
              this.adjustCashbackData = bulkAdjustCashback
            }
          })
      } catch (e) {
        console.log(e)
        if (commit) {
          this.$snackbar({ message: 'Falha ao aprovar ajuste de cashback.', snackbarColor: 'error', timeout: 60000 })
          this.closeDialog()
        } else this.$snackbar({ message: 'Falha ao importar arquivo de ajuste de cashback.', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    closeDialog() {
      this.step = 1
      this.$emit('input', false)
    },
    async submit() {
      this.$v.$touch()
      if (this.$v.$error) {
        return this.$snackbar({ message: 'Ajuste inválido', snackbarColor: 'error' })
      }
      this.loading = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_ADJUST_CASHBACK,
          variables: {
            user_id: this.user._id,
            adjustment_amount: this.parsedAdjustmentValue,
            adjustment_reason: this.adjustmentReason,
            ...(this.adjustmentType === 'CREDIT' && { expires_at: this.expiresAt }),
          },
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.$emit('adjust', false)
        this.$snackbar({ message: 'Ajuste realizado com sucesso.', snackbarColor: 'success' })
      } catch (e) {
        this.$snackbar({ message: 'Erro ao ajustar cashback', snackbarColor: 'error' })
      }
      this.loading = false
      this.$emit('input', false)
    },
    async searchUser() {
      this.loadingUser = true
      this.userCashbackFound = false
      this.userNotFound = false
      let userFound = false
      this.user = {}
      this.user_cashback = {}

      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_USER_QUERY,
          variables: { document_no: this.cleanDocumentNo },
          fetchPolicy: 'no-cache',
        })
        if (data.user) {
          this.user = data.user
          userFound = true
        } else {
          this.$snackbar({ message: 'Usuário não encontrado' })
          this.userNotFound = true
        }
      } catch (e) {
        this.$snackbar({ message: 'Erro ao buscar usuário', snackbarColor: 'error' })
      }

      if (userFound) {
        this.userCashbackFound = await this.searchUserCashback()
      }

      this.loadingUser = false
    },
    async searchUserCashback() {
      try {
        const { data } = await this.$apollo.query({
          query: SEARCH_USER_CASHBACK_QUERY,
          variables: { _id: this.user._id },
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['cashback'],
          },
        })
        this.user_cashback = data.getCashbackByUserId
        return true
      } catch (e) {
        this.$snackbar({ message: 'Erro ao buscar cashback', snackbarColor: 'error' })
        return false
      }
    },
  },
  validations() {
    return {
      adjustmentValue: {
        min: (value) => parseMoney(value) > 0,
        max: (value) => {
          if (this.adjustmentType === 'DEBIT' && parseMoney(value) > this.user_cashback.inactivated_amount) {
            return false
          } else return true
        },
      },
      ...(this.adjustmentType === 'CREDIT' && { expiresAt: { required, notOnlySpace } }),
    }
  },
}
</script>

<style lang="scss" scoped>
.search-container {
  ::v-deep .v-input {
    width: 270px;
  }
}
.dialog-content {
  max-height: 58vh;
  overflow-y: auto;
}
</style>
